import { getMessages, type SupportedLocale } from "@/i18n/runtime";
import { type Notation } from "@/shared/format/numberFormat";
import { createHintTooltip } from "@/shared/ui/hintTooltip";
import type {
  AchievementNotificationMode,
  HoverChargeDuration,
  HoverIdleSettingsState,
  HoverToggleDuration,
  MHovAnimationSpeed,
  MHovChargeDurationMs,
  NHovAnimationSpeed,
  NHovChargeDurationMs,
  OHovChargeDurationMs,
  WallNotificationMode,
} from "@/features/hoverIdle/types";
import type { SettingsAccountSection } from "@/features/settings/settingsAccountSection";

export type SettingsTabId =
  | "general"
  | "gameplay"
  | "display"
  | "notifications"
  | "data";

export interface SettingsPanelRuntime {
  readonly isMHovUnlocked: boolean;
  readonly isNHovUnlocked: boolean;
  readonly isOHovUnlocked: boolean;
}

export interface SettingsPanel {
  readonly root: HTMLDivElement;
  readonly dataContainer: HTMLDivElement;
  setActiveTab(id: SettingsTabId): void;
  update(settings: HoverIdleSettingsState, runtime: SettingsPanelRuntime): void;
  dispose(): void;
}

interface CreateSettingsPanelOptions {
  readonly settings: HoverIdleSettingsState;
  readonly runtime: SettingsPanelRuntime;
  readonly onReduceMotionChange: (reduceMotion: boolean) => void;
  readonly onLowPowerModeChange: (lowPowerMode: boolean) => void;
  readonly onLanguageChange: (language: SupportedLocale) => void;
  readonly onAchievementNotificationChange: (
    mode: AchievementNotificationMode,
  ) => void;
  readonly onWallNotificationModeChange: (mode: WallNotificationMode) => void;
  readonly onHelpNotificationsEnabledChange: (enabled: boolean) => void;
  readonly onNotationChange: (notation: Notation) => void;
  readonly onMHovChargeDurationChange: (value: MHovChargeDurationMs) => void;
  readonly onMHovAnimationSpeedChange: (value: MHovAnimationSpeed) => void;
  readonly onNHovChargeDurationChange: (value: NHovChargeDurationMs) => void;
  readonly onNHovAnimationSpeedChange: (value: NHovAnimationSpeed) => void;
  readonly onOHovChargeDurationChange: (value: OHovChargeDurationMs) => void;
  readonly onOutsideResetBurstAnimationChange: (enabled: boolean) => void;
  readonly onHoverToggleDurationChange: (value: HoverToggleDuration) => void;
  readonly onHoverChargeDurationChange: (value: HoverChargeDuration) => void;
  readonly accountSection?: SettingsAccountSection | null;
}

interface SettingsTabDef {
  readonly id: SettingsTabId;
  readonly draft: boolean;
}

const SETTINGS_TABS: readonly SettingsTabDef[] = [
  { id: "general", draft: false },
  { id: "gameplay", draft: false },
  { id: "display", draft: false },
  { id: "notifications", draft: false },
  { id: "data", draft: false },
];

const FALLBACK_SETTINGS_TAB: SettingsTabId = "general";

export function isSettingsTabVisible(
  tabId: SettingsTabId,
  runtime: SettingsPanelRuntime,
): boolean {
  if (tabId === "gameplay") {
    return (
      runtime.isMHovUnlocked || runtime.isNHovUnlocked || runtime.isOHovUnlocked
    );
  }
  return true;
}

export function pickFallbackSettingsTab(
  activeTabId: SettingsTabId,
  runtime: SettingsPanelRuntime,
): SettingsTabId {
  if (isSettingsTabVisible(activeTabId, runtime)) return activeTabId;
  if (isSettingsTabVisible(FALLBACK_SETTINGS_TAB, runtime)) {
    return FALLBACK_SETTINGS_TAB;
  }
  for (const tab of SETTINGS_TABS) {
    if (isSettingsTabVisible(tab.id, runtime)) return tab.id;
  }
  return FALLBACK_SETTINGS_TAB;
}

export function createSettingsPanel(
  options: CreateSettingsPanelOptions,
): SettingsPanel {
  const messages = getMessages();
  const settingsMessages = messages.settings;
  const root = document.createElement("div");
  root.className = "hover-idle-settings-panel";

  const title = document.createElement("h2");
  title.textContent = settingsMessages.title;

  const tabBar = document.createElement("div");
  tabBar.className = "hover-idle-settings-tabs";
  tabBar.setAttribute("role", "tablist");
  tabBar.setAttribute("aria-label", settingsMessages.categoriesAria);

  const sections = new Map<SettingsTabId, HTMLDivElement>();
  const tabButtons = new Map<SettingsTabId, HTMLButtonElement>();
  const hintTooltips: Array<{ dispose(): void }> = [];

  // ---------------------------------------------------------------------------
  // general tab: language
  // ---------------------------------------------------------------------------
  const generalSection = document.createElement("div");
  generalSection.className = "hover-idle-settings-section";
  generalSection.dataset.section = "general";

  const languageCard = document.createElement("div");
  languageCard.className = "hover-idle-setting-card";

  const languageRow = document.createElement("label");
  languageRow.className = "hover-idle-setting-row";
  const languageCopy = document.createElement("div");
  languageCopy.className = "hover-idle-setting-copy";
  const languageText = document.createElement("span");
  languageText.className = "hover-idle-setting-label";
  languageText.textContent = settingsMessages.general.languageLabel;

  const languageCurrent = document.createElement("span");
  languageCurrent.className = "hover-idle-setting-caption";
  languageCurrent.textContent =
    settingsMessages.general.languageOptions[options.settings.language];

  languageCopy.append(languageText, languageCurrent);

  const languageControl = document.createElement("div");
  languageControl.className = "hover-idle-language-select-wrap";
  const languageSelect = document.createElement("select");
  languageSelect.className = "hover-idle-language-select";
  languageSelect.value = options.settings.language;
  const localeOrder: readonly SupportedLocale[] =
    options.settings.language === "ja" ? ["ja", "en"] : ["en", "ja"];
  for (const locale of localeOrder) {
    const option = document.createElement("option");
    option.value = locale;
    option.textContent = settingsMessages.general.languageOptions[locale];
    languageSelect.append(option);
  }
  const languageDescription = document.createElement("p");
  languageDescription.className = "hover-idle-settings-draft-placeholder";
  languageDescription.textContent =
    settingsMessages.general.languageDescription;
  const handleLanguageChange = (): void => {
    languageCurrent.textContent =
      settingsMessages.general.languageOptions[
        languageSelect.value as SupportedLocale
      ];
    options.onLanguageChange(languageSelect.value as SupportedLocale);
  };
  languageSelect.addEventListener("change", handleLanguageChange);
  languageControl.append(languageSelect);
  languageRow.append(languageCopy, languageControl);
  languageCard.append(languageRow, languageDescription);
  generalSection.append(languageCard);

  // hover-toggle duration: 1 s / 2 s segmented toggle for small-square hover
  // toggle UIs (MP slot assign/unassign, MPM section enabled toggle, TimHov
  // activation toggle). 2 s preserves the legacy charge feel.
  const generalCleanups: Array<() => void> = [];
  const generalSyncs: Array<(settings: HoverIdleSettingsState) => void> = [];
  const hoverToggleDurationValues: readonly HoverToggleDuration[] = [
    "short",
    "normal",
  ];
  const hoverToggleCard = document.createElement("div");
  hoverToggleCard.className = "hover-idle-setting-card";

  const hoverToggleRow = document.createElement("div");
  hoverToggleRow.className = "hover-idle-setting-row";
  const hoverToggleCopy = document.createElement("div");
  hoverToggleCopy.className = "hover-idle-setting-copy";
  const hoverToggleLabel = document.createElement("span");
  hoverToggleLabel.className = "hover-idle-setting-label";
  hoverToggleLabel.textContent =
    settingsMessages.general.hoverToggleDurationLabel;
  hoverToggleCopy.append(hoverToggleLabel);

  const hoverToggleSegmented = document.createElement("div");
  hoverToggleSegmented.className = "hover-idle-segmented";
  hoverToggleSegmented.dataset.setting = "hover-toggle-duration";
  hoverToggleSegmented.setAttribute("role", "radiogroup");
  hoverToggleSegmented.setAttribute(
    "aria-label",
    settingsMessages.general.hoverToggleDurationLabel,
  );

  const hoverToggleButtons = new Map<HoverToggleDuration, HTMLButtonElement>();
  const syncHoverToggleButtons = (value: HoverToggleDuration): void => {
    for (const [optionValue, button] of hoverToggleButtons) {
      const isActive = optionValue === value;
      button.dataset.active = isActive ? "true" : "false";
      button.setAttribute("aria-checked", isActive ? "true" : "false");
    }
  };
  for (const value of hoverToggleDurationValues) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "hover-idle-segmented-option";
    button.dataset.value = value;
    button.setAttribute("role", "radio");
    button.textContent =
      settingsMessages.general.hoverToggleDurationOptions[value];
    const handler = (): void => {
      syncHoverToggleButtons(value);
      options.onHoverToggleDurationChange(value);
    };
    button.addEventListener("click", handler);
    generalCleanups.push(() => button.removeEventListener("click", handler));
    hoverToggleButtons.set(value, button);
    hoverToggleSegmented.append(button);
  }
  syncHoverToggleButtons(options.settings.hoverToggleDuration);
  hoverToggleRow.append(hoverToggleCopy, hoverToggleSegmented);

  const hoverToggleDescription = document.createElement("p");
  hoverToggleDescription.className = "hover-idle-settings-draft-placeholder";
  hoverToggleDescription.textContent =
    settingsMessages.general.hoverToggleDurationDescription;
  hoverToggleCard.append(hoverToggleRow, hoverToggleDescription);
  generalSection.append(hoverToggleCard);
  generalSyncs.push((settings) =>
    syncHoverToggleButtons(settings.hoverToggleDuration),
  );

  // Generic one-shot hover charges have their own timing contract, separate
  // from reversible toggles and feature-owned reset/purchase timings.
  const hoverChargeDurationValues: readonly HoverChargeDuration[] = [
    "short",
    "normal",
  ];
  const hoverChargeCard = document.createElement("div");
  hoverChargeCard.className = "hover-idle-setting-card";

  const hoverChargeRow = document.createElement("div");
  hoverChargeRow.className = "hover-idle-setting-row";
  const hoverChargeCopy = document.createElement("div");
  hoverChargeCopy.className = "hover-idle-setting-copy";
  const hoverChargeLabel = document.createElement("span");
  hoverChargeLabel.className = "hover-idle-setting-label";
  hoverChargeLabel.textContent =
    settingsMessages.general.hoverChargeDurationLabel;
  hoverChargeCopy.append(hoverChargeLabel);

  const hoverChargeSegmented = document.createElement("div");
  hoverChargeSegmented.className = "hover-idle-segmented";
  hoverChargeSegmented.dataset.setting = "hover-charge-duration";
  hoverChargeSegmented.setAttribute("role", "radiogroup");
  hoverChargeSegmented.setAttribute(
    "aria-label",
    settingsMessages.general.hoverChargeDurationLabel,
  );

  const hoverChargeButtons = new Map<HoverChargeDuration, HTMLButtonElement>();
  const syncHoverChargeButtons = (value: HoverChargeDuration): void => {
    for (const [optionValue, button] of hoverChargeButtons) {
      const isActive = optionValue === value;
      button.dataset.active = isActive ? "true" : "false";
      button.setAttribute("aria-checked", isActive ? "true" : "false");
    }
  };
  for (const value of hoverChargeDurationValues) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "hover-idle-segmented-option";
    button.dataset.value = value;
    button.setAttribute("role", "radio");
    button.textContent =
      settingsMessages.general.hoverChargeDurationOptions[value];
    const handler = (): void => {
      syncHoverChargeButtons(value);
      options.onHoverChargeDurationChange(value);
    };
    button.addEventListener("click", handler);
    generalCleanups.push(() => button.removeEventListener("click", handler));
    hoverChargeButtons.set(value, button);
    hoverChargeSegmented.append(button);
  }
  syncHoverChargeButtons(options.settings.hoverChargeDuration);
  hoverChargeRow.append(hoverChargeCopy, hoverChargeSegmented);

  const hoverChargeDescription = document.createElement("p");
  hoverChargeDescription.className = "hover-idle-settings-draft-placeholder";
  hoverChargeDescription.textContent =
    settingsMessages.general.hoverChargeDurationDescription;
  hoverChargeCard.append(hoverChargeRow, hoverChargeDescription);
  generalSection.append(hoverChargeCard);
  generalSyncs.push((settings) =>
    syncHoverChargeButtons(settings.hoverChargeDuration),
  );

  sections.set("general", generalSection);

  // ---------------------------------------------------------------------------
  // gameplay tab: MHov / NHov / OHov reset cards
  // ---------------------------------------------------------------------------
  const gameplaySection = document.createElement("div");
  gameplaySection.className = "hover-idle-settings-section";
  gameplaySection.dataset.section = "gameplay";

  const gameplayCleanups: Array<() => void> = [];
  const gameplaySyncs: Array<(settings: HoverIdleSettingsState) => void> = [];

  type ChargeDurationValue = 0 | 2000 | 5000;
  type AnimationSpeedValue = "normal" | "fast" | "instant";
  const chargeDurationValues: readonly ChargeDurationValue[] = [0, 2000, 5000];
  const animationSpeedValues: readonly AnimationSpeedValue[] = [
    "instant",
    "fast",
    "normal",
  ];

  interface ResetCardOptions {
    readonly scope: "mhov" | "nhov" | "ohov";
    readonly sectionTitle: string;
    readonly charge: {
      readonly label: string;
      readonly hintText: string;
      readonly optionLabels: Readonly<Record<"0" | "2000" | "5000", string>>;
      readonly getValue: (s: HoverIdleSettingsState) => ChargeDurationValue;
      readonly onChange: (value: ChargeDurationValue) => void;
    };
    readonly animation?: {
      readonly label: string;
      readonly optionLabels: Readonly<Record<AnimationSpeedValue, string>>;
      readonly reduceMotionTooltip: string;
      readonly getValue: (s: HoverIdleSettingsState) => AnimationSpeedValue;
      readonly onChange: (value: AnimationSpeedValue) => void;
    };
    // Outside-reset burst on/off toggle. Outside-family common (currently OHov);
    // under reduce motion it is force-overridden to off.
    readonly burst?: {
      readonly label: string;
      readonly optionLabels: Readonly<Record<"on" | "off", string>>;
      readonly reduceMotionTooltip: string;
      readonly getValue: (s: HoverIdleSettingsState) => boolean;
      readonly onChange: (value: boolean) => void;
    };
  }

  const buildResetCard = (opts: ResetCardOptions): HTMLDivElement => {
    const card = document.createElement("div");
    card.className = "hover-idle-setting-card";
    card.dataset.scope = opts.scope;

    const titleEl = document.createElement("h3");
    titleEl.className = "hover-idle-setting-section-title";
    titleEl.textContent = opts.sectionTitle;
    card.append(titleEl);

    // charge duration row
    {
      const row = document.createElement("div");
      row.className = "hover-idle-setting-row";
      const copy = document.createElement("div");
      copy.className = "hover-idle-setting-copy";
      const labelEl = document.createElement("span");
      labelEl.className = "hover-idle-setting-label";
      labelEl.textContent = opts.charge.label;

      const hint = createHintTooltip({
        id: `hover-idle-settings-${opts.scope}-charge-duration-hint`,
        label: messages.common.hintLabel,
        text: opts.charge.hintText,
      });
      hintTooltips.push(hint);

      const labelWithHint = document.createElement("span");
      labelWithHint.className = "hover-idle-setting-label-with-hint";
      labelWithHint.append(labelEl, hint.root);
      copy.append(labelWithHint);

      const segmented = document.createElement("div");
      segmented.className = "hover-idle-segmented";
      segmented.dataset.setting = `${opts.scope}-charge-duration`;
      segmented.setAttribute("role", "radiogroup");
      segmented.setAttribute("aria-label", opts.charge.label);

      const buttons = new Map<ChargeDurationValue, HTMLButtonElement>();
      const syncButtons = (value: ChargeDurationValue): void => {
        for (const [optionValue, button] of buttons) {
          const isActive = optionValue === value;
          button.dataset.active = isActive ? "true" : "false";
          button.setAttribute("aria-checked", isActive ? "true" : "false");
        }
      };
      for (const value of chargeDurationValues) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "hover-idle-segmented-option";
        button.dataset.value = String(value);
        button.setAttribute("role", "radio");
        const key = String(value) as "0" | "2000" | "5000";
        button.textContent = opts.charge.optionLabels[key];
        const handler = (): void => {
          syncButtons(value);
          opts.charge.onChange(value);
        };
        button.addEventListener("click", handler);
        gameplayCleanups.push(() =>
          button.removeEventListener("click", handler),
        );
        buttons.set(value, button);
        segmented.append(button);
      }
      syncButtons(opts.charge.getValue(options.settings));
      row.append(copy, segmented);
      card.append(row);

      gameplaySyncs.push((settings) =>
        syncButtons(opts.charge.getValue(settings)),
      );
    }

    // animation speed row (optional)
    if (opts.animation !== undefined) {
      const animation = opts.animation;
      const row = document.createElement("div");
      row.className = "hover-idle-setting-row";
      const copy = document.createElement("div");
      copy.className = "hover-idle-setting-copy";
      const labelEl = document.createElement("span");
      labelEl.className = "hover-idle-setting-label";
      labelEl.textContent = animation.label;
      copy.append(labelEl);

      const segmented = document.createElement("div");
      segmented.className = "hover-idle-segmented";
      segmented.dataset.setting = `${opts.scope}-animation-speed`;
      segmented.setAttribute("role", "radiogroup");
      segmented.setAttribute("aria-label", animation.label);

      const disabledReason = document.createElement("span");
      disabledReason.id = `hover-idle-settings-${opts.scope}-animation-speed-disabled`;
      disabledReason.className = "hover-idle-setting-disabled-tooltip";
      disabledReason.dataset.setting = `${opts.scope}-animation-speed-disabled-reason`;
      disabledReason.setAttribute("role", "tooltip");
      disabledReason.textContent = animation.reduceMotionTooltip;

      const segmentedWrap = document.createElement("div");
      segmentedWrap.className = "hover-idle-setting-control-tooltip-wrap";
      segmentedWrap.setAttribute("aria-describedby", disabledReason.id);

      const buttons = new Map<AnimationSpeedValue, HTMLButtonElement>();
      let isDisabled = options.settings.reduceMotion;
      const syncButtons = (value: AnimationSpeedValue): void => {
        for (const [optionValue, button] of buttons) {
          const isActive = optionValue === value;
          button.dataset.active = isActive ? "true" : "false";
          button.setAttribute("aria-checked", isActive ? "true" : "false");
          button.disabled = isDisabled;
        }
      };
      const syncDisabledState = (reduceMotion: boolean): void => {
        isDisabled = reduceMotion;
        for (const button of buttons.values()) {
          button.disabled = reduceMotion;
        }
      };
      const syncDisabledTooltip = (reduceMotion: boolean): void => {
        segmentedWrap.dataset.disabled = reduceMotion ? "true" : "false";
        segmentedWrap.tabIndex = reduceMotion ? 0 : -1;
        disabledReason.setAttribute(
          "aria-hidden",
          reduceMotion ? "false" : "true",
        );
      };
      for (const value of animationSpeedValues) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "hover-idle-segmented-option";
        button.dataset.value = value;
        button.setAttribute("role", "radio");
        button.textContent = animation.optionLabels[value];
        const handler = (): void => {
          syncButtons(value);
          animation.onChange(value);
        };
        button.addEventListener("click", handler);
        gameplayCleanups.push(() =>
          button.removeEventListener("click", handler),
        );
        buttons.set(value, button);
        segmented.append(button);
      }
      segmentedWrap.append(segmented, disabledReason);
      syncDisabledTooltip(options.settings.reduceMotion);
      syncButtons(animation.getValue(options.settings));
      row.append(copy, segmentedWrap);
      card.append(row);

      gameplaySyncs.push((settings) => {
        syncDisabledState(settings.reduceMotion);
        syncDisabledTooltip(settings.reduceMotion);
        syncButtons(animation.getValue(settings));
      });
    }

    // outside-reset burst toggle row (optional)
    if (opts.burst !== undefined) {
      const burst = opts.burst;
      const row = document.createElement("div");
      row.className = "hover-idle-setting-row";
      const copy = document.createElement("div");
      copy.className = "hover-idle-setting-copy";
      const labelEl = document.createElement("span");
      labelEl.className = "hover-idle-setting-label";
      labelEl.textContent = burst.label;
      copy.append(labelEl);

      const segmented = document.createElement("div");
      segmented.className = "hover-idle-segmented";
      segmented.dataset.setting = `${opts.scope}-burst-animation`;
      segmented.setAttribute("role", "radiogroup");
      segmented.setAttribute("aria-label", burst.label);

      const disabledReason = document.createElement("span");
      disabledReason.id = `hover-idle-settings-${opts.scope}-burst-animation-disabled`;
      disabledReason.className = "hover-idle-setting-disabled-tooltip";
      disabledReason.dataset.setting = `${opts.scope}-burst-animation-disabled-reason`;
      disabledReason.setAttribute("role", "tooltip");
      disabledReason.textContent = burst.reduceMotionTooltip;

      const segmentedWrap = document.createElement("div");
      segmentedWrap.className = "hover-idle-setting-control-tooltip-wrap";
      segmentedWrap.setAttribute("aria-describedby", disabledReason.id);

      const buttons = new Map<boolean, HTMLButtonElement>();
      let isDisabled = options.settings.reduceMotion;
      const syncButtons = (value: boolean): void => {
        // Reduce motion force-overrides the burst to off, like MHov animation.
        const shownValue = isDisabled ? false : value;
        for (const [optionValue, button] of buttons) {
          const isActive = optionValue === shownValue;
          button.dataset.active = isActive ? "true" : "false";
          button.setAttribute("aria-checked", isActive ? "true" : "false");
          button.disabled = isDisabled;
        }
      };
      const syncDisabledTooltip = (reduceMotion: boolean): void => {
        segmentedWrap.dataset.disabled = reduceMotion ? "true" : "false";
        segmentedWrap.tabIndex = reduceMotion ? 0 : -1;
        disabledReason.setAttribute(
          "aria-hidden",
          reduceMotion ? "false" : "true",
        );
      };
      for (const value of [false, true]) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "hover-idle-segmented-option";
        button.dataset.value = value ? "on" : "off";
        button.setAttribute("role", "radio");
        button.textContent = burst.optionLabels[value ? "on" : "off"];
        const handler = (): void => {
          syncButtons(value);
          burst.onChange(value);
        };
        button.addEventListener("click", handler);
        gameplayCleanups.push(() =>
          button.removeEventListener("click", handler),
        );
        buttons.set(value, button);
        segmented.append(button);
      }
      segmentedWrap.append(segmented, disabledReason);
      syncDisabledTooltip(options.settings.reduceMotion);
      syncButtons(burst.getValue(options.settings));
      row.append(copy, segmentedWrap);
      card.append(row);

      gameplaySyncs.push((settings) => {
        isDisabled = settings.reduceMotion;
        syncDisabledTooltip(settings.reduceMotion);
        syncButtons(burst.getValue(settings));
      });
    }

    return card;
  };

  const mHovCard = buildResetCard({
    scope: "mhov",
    sectionTitle: settingsMessages.gameplay.mHov.sectionTitle,
    charge: {
      label: settingsMessages.gameplay.mHov.chargeDurationLabel,
      hintText: settingsMessages.gameplay.mHov.chargeDurationHint,
      optionLabels: settingsMessages.gameplay.mHov.chargeDurationOptions,
      getValue: (s) => s.mHovChargeDurationMs,
      onChange: (v) => options.onMHovChargeDurationChange(v),
    },
    animation: {
      label: settingsMessages.gameplay.mHov.animationSpeedLabel,
      optionLabels: settingsMessages.gameplay.mHov.animationSpeedOptions,
      reduceMotionTooltip:
        settingsMessages.gameplay.mHov.animationSpeedReduceMotionTooltip,
      getValue: (s) => s.mHovAnimationSpeed,
      onChange: (v) => options.onMHovAnimationSpeedChange(v),
    },
  });

  const nHovCard = buildResetCard({
    scope: "nhov",
    sectionTitle: settingsMessages.gameplay.nHov.sectionTitle,
    charge: {
      label: settingsMessages.gameplay.nHov.chargeDurationLabel,
      hintText: settingsMessages.gameplay.nHov.chargeDurationHint,
      optionLabels: settingsMessages.gameplay.nHov.chargeDurationOptions,
      getValue: (s) => s.nHovChargeDurationMs,
      onChange: (v) => options.onNHovChargeDurationChange(v),
    },
    animation: {
      label: settingsMessages.gameplay.nHov.animationSpeedLabel,
      optionLabels: settingsMessages.gameplay.nHov.animationSpeedOptions,
      reduceMotionTooltip:
        settingsMessages.gameplay.nHov.animationSpeedReduceMotionTooltip,
      getValue: (s) => s.nHovAnimationSpeed,
      onChange: (v) => options.onNHovAnimationSpeedChange(v),
    },
  });

  const oHovCard = buildResetCard({
    scope: "ohov",
    sectionTitle: settingsMessages.gameplay.oHov.sectionTitle,
    charge: {
      label: settingsMessages.gameplay.oHov.chargeDurationLabel,
      hintText: settingsMessages.gameplay.oHov.chargeDurationHint,
      optionLabels: settingsMessages.gameplay.oHov.chargeDurationOptions,
      getValue: (s) => s.oHovChargeDurationMs,
      onChange: (v) => options.onOHovChargeDurationChange(v),
    },
    burst: {
      label: settingsMessages.gameplay.oHov.burstAnimationLabel,
      optionLabels: settingsMessages.gameplay.oHov.burstAnimationOptions,
      reduceMotionTooltip:
        settingsMessages.gameplay.oHov.burstAnimationReduceMotionTooltip,
      getValue: (s) => s.outsideResetBurstAnimationEnabled,
      onChange: (v) => options.onOutsideResetBurstAnimationChange(v),
    },
  });

  gameplaySection.append(mHovCard, nHovCard, oHovCard);
  sections.set("gameplay", gameplaySection);

  // ---------------------------------------------------------------------------
  // display tab: motion, power, and number notation
  // ---------------------------------------------------------------------------
  const displaySection = document.createElement("div");
  displaySection.className = "hover-idle-settings-section";
  displaySection.dataset.section = "display";

  const reduceMotionRow = document.createElement("div");
  reduceMotionRow.className = "hover-idle-setting-row";

  const checkbox = document.createElement("input");
  checkbox.type = "checkbox";
  checkbox.id = "hover-idle-settings-reduce-motion";
  checkbox.checked = options.settings.reduceMotion;
  checkbox.dataset.setting = "reduce-motion";

  const text = document.createElement("label");
  text.className = "hover-idle-setting-label";
  text.htmlFor = checkbox.id;
  text.textContent = settingsMessages.display.reduceMotion;

  const reduceMotionHint = createHintTooltip({
    id: "hover-idle-settings-reduce-motion-hint",
    label: messages.common.hintLabel,
    text: settingsMessages.display.reduceMotionHint,
  });
  hintTooltips.push(reduceMotionHint);

  const reduceMotionLabelWithHint = document.createElement("span");
  reduceMotionLabelWithHint.className = "hover-idle-setting-label-with-hint";
  reduceMotionLabelWithHint.append(text, reduceMotionHint.root);

  const handleChange = (): void => {
    options.onReduceMotionChange(checkbox.checked);
  };

  checkbox.addEventListener("change", handleChange);
  reduceMotionRow.append(checkbox, reduceMotionLabelWithHint);
  displaySection.append(reduceMotionRow);

  const lowPowerModeRow = document.createElement("div");
  lowPowerModeRow.className = "hover-idle-setting-row";

  const lowPowerModeCheckbox = document.createElement("input");
  lowPowerModeCheckbox.type = "checkbox";
  lowPowerModeCheckbox.id = "hover-idle-settings-low-power-mode";
  lowPowerModeCheckbox.checked = options.settings.lowPowerMode;
  lowPowerModeCheckbox.dataset.setting = "low-power-mode";

  const lowPowerModeText = document.createElement("label");
  lowPowerModeText.className = "hover-idle-setting-label";
  lowPowerModeText.htmlFor = lowPowerModeCheckbox.id;
  lowPowerModeText.textContent = settingsMessages.display.lowPowerMode;

  const lowPowerModeHint = createHintTooltip({
    id: "hover-idle-settings-low-power-mode-hint",
    label: messages.common.hintLabel,
    text: settingsMessages.display.lowPowerModeHint,
  });
  hintTooltips.push(lowPowerModeHint);

  const lowPowerModeLabelWithHint = document.createElement("span");
  lowPowerModeLabelWithHint.className = "hover-idle-setting-label-with-hint";
  lowPowerModeLabelWithHint.append(lowPowerModeText, lowPowerModeHint.root);

  const handleLowPowerModeChange = (): void => {
    options.onLowPowerModeChange(lowPowerModeCheckbox.checked);
  };

  lowPowerModeCheckbox.addEventListener("change", handleLowPowerModeChange);
  lowPowerModeRow.append(lowPowerModeCheckbox, lowPowerModeLabelWithHint);
  displaySection.append(lowPowerModeRow);

  const notationCard = document.createElement("div");
  notationCard.className = "hover-idle-setting-card";
  const notationRow = document.createElement("label");
  notationRow.className = "hover-idle-setting-row";
  const notationCopy = document.createElement("div");
  notationCopy.className = "hover-idle-setting-copy";
  const notationText = document.createElement("span");
  notationText.className = "hover-idle-setting-label";
  notationText.textContent = settingsMessages.display.notationLabel;
  const notationCaption = document.createElement("span");
  notationCaption.className = "hover-idle-setting-caption";
  notationCaption.textContent =
    settingsMessages.display.notationOptions[options.settings.notation];
  notationCopy.append(notationText, notationCaption);

  const notationControl = document.createElement("div");
  notationControl.className = "hover-idle-language-select-wrap";
  const notationSelect = document.createElement("select");
  notationSelect.className = "hover-idle-language-select";
  notationSelect.dataset.setting = "notation";
  notationSelect.value = options.settings.notation;
  const notationOrder: readonly Notation[] = [
    "standard",
    "scientific",
    "engineering",
  ];
  for (const id of notationOrder) {
    const option = document.createElement("option");
    option.value = id;
    option.textContent = settingsMessages.display.notationOptions[id];
    notationSelect.append(option);
  }
  notationSelect.value = options.settings.notation;
  const handleNotationChange = (): void => {
    const next = notationSelect.value as Notation;
    notationCaption.textContent =
      settingsMessages.display.notationOptions[next];
    options.onNotationChange(next);
  };
  notationSelect.addEventListener("change", handleNotationChange);
  notationControl.append(notationSelect);
  notationRow.append(notationCopy, notationControl);
  notationCard.append(notationRow);
  displaySection.append(notationCard);
  sections.set("display", displaySection);

  // ---------------------------------------------------------------------------
  // notifications tab
  // ---------------------------------------------------------------------------
  const notificationsSection = document.createElement("div");
  notificationsSection.className = "hover-idle-settings-section";
  notificationsSection.dataset.section = "notifications";

  const notificationsCard = document.createElement("div");
  notificationsCard.className = "hover-idle-setting-card";

  const notificationsRow = document.createElement("div");
  notificationsRow.className = "hover-idle-setting-row";

  const notificationsCopy = document.createElement("div");
  notificationsCopy.className = "hover-idle-setting-copy";
  const notificationsLabel = document.createElement("span");
  notificationsLabel.className = "hover-idle-setting-label";
  notificationsLabel.textContent =
    settingsMessages.notifications.achievementNotificationLabel;
  notificationsCopy.append(notificationsLabel);

  const segmented = document.createElement("div");
  segmented.className = "hover-idle-segmented";
  segmented.setAttribute("role", "radiogroup");
  segmented.setAttribute(
    "aria-label",
    settingsMessages.notifications.achievementNotificationLabel,
  );

  const achievementNotifOptions: readonly AchievementNotificationMode[] = [
    "off",
    "dot",
    "full",
  ];
  const achievementNotifButtons = new Map<
    AchievementNotificationMode,
    HTMLButtonElement
  >();
  const achievementNotifCleanups: Array<() => void> = [];
  const syncAchievementNotifButtons = (
    mode: AchievementNotificationMode,
  ): void => {
    for (const [value, button] of achievementNotifButtons) {
      const isActive = value === mode;
      button.dataset.active = isActive ? "true" : "false";
      button.setAttribute("aria-checked", isActive ? "true" : "false");
    }
  };
  for (const mode of achievementNotifOptions) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "hover-idle-segmented-option";
    button.dataset.value = mode;
    button.setAttribute("role", "radio");
    button.textContent =
      settingsMessages.notifications.achievementNotificationOptions[mode];
    const handler = (): void => {
      syncAchievementNotifButtons(mode);
      options.onAchievementNotificationChange(mode);
    };
    button.addEventListener("click", handler);
    achievementNotifCleanups.push(() =>
      button.removeEventListener("click", handler),
    );
    achievementNotifButtons.set(mode, button);
    segmented.append(button);
  }
  syncAchievementNotifButtons(options.settings.achievementNotification);

  const wallNotifRow = document.createElement("div");
  wallNotifRow.className = "hover-idle-setting-row";

  const wallNotifCopy = document.createElement("div");
  wallNotifCopy.className = "hover-idle-setting-copy";
  const wallNotifLabel = document.createElement("span");
  wallNotifLabel.className = "hover-idle-setting-label";
  wallNotifLabel.textContent =
    settingsMessages.notifications.wallNotificationLabel;
  wallNotifCopy.append(wallNotifLabel);

  const wallNotifSegmented = document.createElement("div");
  wallNotifSegmented.className = "hover-idle-segmented";
  wallNotifSegmented.setAttribute("role", "radiogroup");
  wallNotifSegmented.setAttribute(
    "aria-label",
    settingsMessages.notifications.wallNotificationLabel,
  );

  const wallNotifOptions: readonly WallNotificationMode[] = [
    "off",
    "dot",
    "full",
  ];
  const wallNotifButtons = new Map<WallNotificationMode, HTMLButtonElement>();
  const wallNotifCleanups: Array<() => void> = [];
  const syncWallNotifButtons = (mode: WallNotificationMode): void => {
    for (const [value, button] of wallNotifButtons) {
      const isActive = value === mode;
      button.dataset.active = isActive ? "true" : "false";
      button.setAttribute("aria-checked", isActive ? "true" : "false");
    }
  };
  for (const mode of wallNotifOptions) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "hover-idle-segmented-option";
    button.dataset.value = mode;
    button.dataset.setting = "wall-notification";
    button.setAttribute("role", "radio");
    button.textContent =
      settingsMessages.notifications.wallNotificationOptions[mode];
    const handler = (): void => {
      syncWallNotifButtons(mode);
      options.onWallNotificationModeChange(mode);
    };
    button.addEventListener("click", handler);
    wallNotifCleanups.push(() => button.removeEventListener("click", handler));
    wallNotifButtons.set(mode, button);
    wallNotifSegmented.append(button);
  }
  syncWallNotifButtons(options.settings.wallNotificationMode);
  wallNotifRow.append(wallNotifCopy, wallNotifSegmented);

  const helpNotificationsRow = document.createElement("div");
  helpNotificationsRow.className = "hover-idle-setting-row";

  const helpNotificationsCopy = document.createElement("div");
  helpNotificationsCopy.className = "hover-idle-setting-copy";
  const helpNotificationsLabel = document.createElement("label");
  helpNotificationsLabel.className = "hover-idle-setting-label";
  helpNotificationsLabel.htmlFor = "hover-idle-settings-help-notifications";
  helpNotificationsLabel.textContent =
    settingsMessages.notifications.helpNotificationsLabel;
  const helpNotificationsDescription = document.createElement("span");
  helpNotificationsDescription.className = "hover-idle-setting-caption";
  helpNotificationsDescription.textContent =
    settingsMessages.notifications.helpNotificationsDescription;
  helpNotificationsCopy.append(
    helpNotificationsLabel,
    helpNotificationsDescription,
  );

  const helpNotificationsCheckbox = document.createElement("input");
  helpNotificationsCheckbox.type = "checkbox";
  helpNotificationsCheckbox.id = "hover-idle-settings-help-notifications";
  helpNotificationsCheckbox.checked = options.settings.helpNotificationsEnabled;
  helpNotificationsCheckbox.dataset.setting = "help-notifications";
  const handleHelpNotificationsChange = (): void => {
    options.onHelpNotificationsEnabledChange(helpNotificationsCheckbox.checked);
  };
  helpNotificationsCheckbox.addEventListener(
    "change",
    handleHelpNotificationsChange,
  );

  helpNotificationsRow.append(helpNotificationsCopy, helpNotificationsCheckbox);
  notificationsRow.append(notificationsCopy, segmented);
  notificationsCard.append(
    notificationsRow,
    wallNotifRow,
    helpNotificationsRow,
  );
  notificationsSection.append(notificationsCard);
  sections.set("notifications", notificationsSection);

  // ---------------------------------------------------------------------------
  // data tab: account section + external mount point for backup / load /
  // reward / danger UI
  // ---------------------------------------------------------------------------
  const dataSection = document.createElement("div");
  dataSection.className = "hover-idle-settings-section";
  dataSection.dataset.section = "data";

  if (options.accountSection) {
    dataSection.append(options.accountSection.root);
  }

  const dataContainer = document.createElement("div");
  dataContainer.className = "hover-idle-settings-data-slot";
  dataSection.append(dataContainer);
  sections.set("data", dataSection);

  // ---------------------------------------------------------------------------
  // tabs wiring
  // ---------------------------------------------------------------------------
  let currentRuntime: SettingsPanelRuntime = options.runtime;
  let activeTabId: SettingsTabId = pickFallbackSettingsTab(
    "general",
    currentRuntime,
  );
  const tabCleanups: Array<() => void> = [];

  const applyTabVisibility = (): void => {
    mHovCard.hidden = !currentRuntime.isMHovUnlocked;
    nHovCard.hidden = !currentRuntime.isNHovUnlocked;
    oHovCard.hidden = !currentRuntime.isOHovUnlocked;
    for (const tab of SETTINGS_TABS) {
      const visible = isSettingsTabVisible(tab.id, currentRuntime);
      const button = tabButtons.get(tab.id);
      const section = sections.get(tab.id)!;
      if (button !== undefined) button.hidden = !visible;
      if (!visible) section.hidden = true;
    }
    if (!isSettingsTabVisible(activeTabId, currentRuntime)) {
      const fallback = pickFallbackSettingsTab(activeTabId, currentRuntime);
      setActiveTabInternal(fallback);
    } else {
      const activeSection = sections.get(activeTabId);
      if (activeSection !== undefined) activeSection.hidden = false;
    }
  };

  const setActiveTabInternal = (id: SettingsTabId): void => {
    sections.get(activeTabId)!.hidden = true;
    sections.get(id)!.hidden = false;
    for (const [tabId, button] of tabButtons) {
      button.dataset.active = tabId === id ? "true" : "false";
      button.setAttribute("aria-selected", tabId === id ? "true" : "false");
    }
    activeTabId = id;
  };

  const setActiveTab = (id: SettingsTabId): void => {
    if (!isSettingsTabVisible(id, currentRuntime)) return;
    if (id === activeTabId) return;
    setActiveTabInternal(id);
  };

  for (const tab of SETTINGS_TABS) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "hover-idle-settings-tab";
    if (tab.draft) button.classList.add("hover-idle-settings-tab-draft");
    button.textContent = settingsMessages.tabs[tab.id];
    button.dataset.tab = tab.id;
    button.dataset.active = tab.id === activeTabId ? "true" : "false";
    button.setAttribute("role", "tab");
    button.setAttribute(
      "aria-selected",
      tab.id === activeTabId ? "true" : "false",
    );
    const handler = (): void => setActiveTab(tab.id);
    button.addEventListener("click", handler);
    tabCleanups.push(() => button.removeEventListener("click", handler));
    tabButtons.set(tab.id, button);
    tabBar.append(button);

    const section = sections.get(tab.id)!;
    if (tab.id !== activeTabId) section.hidden = true;
  }

  applyTabVisibility();

  root.append(title, tabBar);
  for (const tab of SETTINGS_TABS) {
    root.append(sections.get(tab.id)!);
  }

  return {
    root,
    dataContainer,
    setActiveTab(id): void {
      setActiveTab(id);
    },
    update(settings, runtime): void {
      currentRuntime = runtime;
      checkbox.checked = settings.reduceMotion;
      lowPowerModeCheckbox.checked = settings.lowPowerMode;
      languageSelect.value = settings.language;
      languageCurrent.textContent =
        settingsMessages.general.languageOptions[settings.language];
      syncAchievementNotifButtons(settings.achievementNotification);
      syncWallNotifButtons(settings.wallNotificationMode);
      helpNotificationsCheckbox.checked = settings.helpNotificationsEnabled;
      notationSelect.value = settings.notation;
      notationCaption.textContent =
        settingsMessages.display.notationOptions[settings.notation];
      for (const sync of generalSyncs) sync(settings);
      for (const sync of gameplaySyncs) sync(settings);
      applyTabVisibility();
    },
    dispose(): void {
      checkbox.removeEventListener("change", handleChange);
      lowPowerModeCheckbox.removeEventListener(
        "change",
        handleLowPowerModeChange,
      );
      languageSelect.removeEventListener("change", handleLanguageChange);
      notationSelect.removeEventListener("change", handleNotationChange);
      helpNotificationsCheckbox.removeEventListener(
        "change",
        handleHelpNotificationsChange,
      );
      for (const cleanup of tabCleanups) cleanup();
      for (const cleanup of achievementNotifCleanups) cleanup();
      for (const cleanup of wallNotifCleanups) cleanup();
      for (const cleanup of generalCleanups) cleanup();
      for (const cleanup of gameplayCleanups) cleanup();
      for (const hintTooltip of hintTooltips) hintTooltip.dispose();
      root.remove();
    },
  };
}
